import { Link } from "react-router-dom";
import { Users, LockKeyhole, CalendarDays, CalendarCheck, MessageSquare, Info, Megaphone, UsersRound, CalendarX2 } from "lucide-react";

import { cn } from "@/lib/utils";
import { text, own } from "@/lib/memberDerive";
import { greeting, nextConfirmed, isSimplified, firstIncoming } from "@/lib/v3Views";
import { useMemberAuth } from "../MemberAuthContext";
import { useCore } from "../useCore";
import { shortName } from "../navSections";
import { ConnectionError } from "../components/ConnectionError";
import { AnnouncementImage } from "../components/AnnouncementImage";
import { Loading, Avatar } from "../components/kit";
import { V3Title, V3Columns, AsideCard, V3_PRIMARY } from "../components/v3";
import { V3_CARD, NextActivityCard, NearbyCard, ActionTile, SectionTitle, AreaCard, ResponsibleCard } from "../components/v3app";

const INTRO = "Dein nächster bestätigter Termin und passende Ideen in deiner Nähe.";

// The Core always returns these three arrays, including explicit empty arrays.
// Check collection shape and row identities before rendering; this is not a full field schema.
function feedData(data) {
  if (!data || typeof data !== "object" || Array.isArray(data)) throw new Error("Unreadable start feed");
  for (const key of ["announcements", "upcoming_activities", "suggested_groups"]) {
    const rows = own(data, key);
    if (!Array.isArray(rows)) throw new Error("Unreadable start feed collection");
    const ids = new Set();
    for (const row of rows) {
      if (!row || typeof row !== "object" || Array.isArray(row)) throw new Error("Unreadable start feed entry");
      const id = own(row, "id");
      const valid = typeof id === "number" ? Number.isFinite(id)
        : typeof id === "string" && id.trim() !== "" && !/[\u0000-\u001f\u007f]/.test(id);
      if (!valid || ids.has(String(id))) throw new Error("Unreadable start feed identity");
      ids.add(String(id));
    }
  }
  return data;
}

function NoConfirmed() {
  return (
    <div data-testid="start-next-empty" className={`${V3_CARD} flex flex-col gap-[14px] px-[20px] py-[22px]`}>
      <p className="flex items-center gap-[12px] text-[19px] font-bold text-v3-ink"><CalendarX2 className="h-[22px] w-[22px] text-v3-magenta" aria-hidden="true" />Noch kein bestätigter Termin</p>
      <p className="text-[14px] leading-[20px] text-v3-muted">Sobald eine Teilnahme bestätigt ist, erscheint dein nächster Termin hier.</p>
      <Link to="/aktivitaeten" data-testid="start-next-discover" className={V3_PRIMARY}>Aktivitäten entdecken</Link>
    </div>
  );
}

// Real next step: an incoming contact request if the Core lists one, otherwise the voluntary setup.
function NextStep({ request }) {
  const o = request?.other || {};
  const first = typeof o.name === "string" ? o.name.trim().split(/\s+/)[0] : "";
  const label = shortName(o.name);
  return (
    <div data-testid="start-next-step">
      <div className="rounded-[12px] border border-v3-line bg-v3-blush px-[20px] pb-[25px] pt-[15px]">
        <p className="flex items-center gap-[12px] text-[17px] font-bold leading-[24px] text-v3-ink"><Users className="h-[18px] w-[18px] shrink-0 text-v3-magenta" aria-hidden="true" />
          {request ? <><span className="lg:hidden">Eine Kontaktanfrage wartet</span><span className="hidden lg:inline">Dein nächster Schritt</span></> : "Dein nächster Schritt"}</p>
        <p className="mt-[8px] text-[14px] leading-[20px] text-v3-muted" data-testid="start-next-step-text">
          {request ? `${first || "Ein Mitglied"} möchte dich kennenlernen.` : "Profil, Interessen und Darstellung einrichten – freiwillig, jederzeit änderbar."}</p>
      </div>
      {request && label && <p className="mt-[14px] hidden items-center gap-[14px] px-[20px] text-[15px] font-bold text-v3-ink lg:flex">
        <Avatar name={o.name} color={o.avatar_color} size="h-[54px] w-[54px] text-sm" />{o.city ? `${label} · ${o.city}` : label}</p>}
      <div className="mt-[9px] px-[16px] lg:mt-[16px] lg:px-[18px]">
        <Link to={request ? "/kontakte" : "/einrichtung"} data-testid={request ? "start-open-request" : "start-setup-link"} className={cn(V3_PRIMARY, "lg:w-full")}>{request ? "Anfrage ansehen" : "Profil einrichten"}</Link>
      </div>
    </div>
  );
}

function Announcements({ list }) {
  if (!list.length) return null;
  return (
    <section aria-labelledby="start-hints" data-testid="member-announcements" className="space-y-[11px]">
      <h2 id="start-hints" className="flex items-center gap-[10px] text-[19px] font-bold text-v3-ink"><Megaphone className="h-[18px] w-[18px] text-v3-magenta" aria-hidden="true" />Hinweise von VISIBILIS</h2>
      {list.map((a) => <div key={a.id} className={`${V3_CARD} px-[18px] py-[14px]`}><p className="font-bold text-v3-ink">{text(a.title)}</p><p className="mt-2 text-lg leading-relaxed text-v3-muted">{text(a.body)}</p><AnnouncementImage announcement={a} /></div>)}
    </section>
  );
}

function SuggestedGroups({ groups }) {
  if (!groups.length) return null;
  return (
    <section aria-labelledby="start-groups-h" data-testid="start-groups" className="space-y-[11px]">
      <h2 id="start-groups-h" className="flex items-center gap-[10px] text-[19px] font-bold text-v3-ink"><UsersRound className="h-[18px] w-[18px] text-v3-magenta" aria-hidden="true" />Kreise für dich</h2>
      <ul className="grid gap-[11px] sm:grid-cols-2">{groups.map((g) => (
        <li key={g.id}><Link to={`/kreise/${encodeURIComponent(g.id)}`} className={`${V3_CARD} block px-[18px] py-[14px] transition-colors hover:border-v3-magenta`}>
          <span className="block font-bold text-v3-ink">{text(g.name)}</span>
          {Number.isInteger(g.members_count) && <span className="text-[14px] text-v3-muted">{g.members_count} Mitglieder</span>}</Link></li>))}</ul>
    </section>
  );
}

// M114 – only when the Core reports prefs.display.simplified === true.
function SimpleStart() {
  return (
    <div data-testid="start-simplified" data-m="M114">
      <V3Title title="Was möchtest du tun?" intro="Was möchtest du als Nächstes tun?" testid="start-title" />
      <div className="space-y-[18px]">
        <ActionTile to="/aktivitaeten" icon={CalendarDays} title="Aktivität finden" text="Termine und Orte ansehen" testid="simple-activities" />
        <ActionTile to="/plaene" icon={Users} title="Meine Treffen" text="Bestätigte Termine öffnen" testid="simple-plans" />
        <ActionTile to="/nachrichten" icon={MessageSquare} title="Nachrichten lesen" text="Bestätigte Kontakte und Kreise" testid="simple-messages" />
        <ActionTile to="/hilfe" icon={Info} title="Hilfe erhalten" text="Schrittweise Unterstützung" testid="simple-help" />
      </div>
    </div>
  );
}

function StartAside({ request }) {
  return (
    <aside aria-label="Für dich" className="min-w-0 space-y-[18px] lg:col-start-2 lg:row-span-2 lg:row-start-1">
      <NextStep request={request} />
      <div className="rounded-[12px] bg-v3-lilac px-[20px] pb-[22px] pt-[12px]" data-testid="start-pass-private">
        <p className="flex items-center gap-[12px] text-[16px] font-bold leading-[22px] text-v3-ink"><LockKeyhole className="h-[18px] w-[18px] shrink-0" aria-hidden="true" />Dein Access Pass bleibt privat</p>
        <p className="mt-[9px] text-[14px] leading-[20px] text-v3-muted">Du entscheidest über Person, Angaben und Dauer jeder Freigabe.</p>
      </div>
      <div className="hidden lg:block"><AsideCard icon={CalendarCheck} tone="white" title="Deine Planung" text="Termin und öffentlicher Treffpunkt bleiben in deinen Plänen zusammen." testid="start-planning" /></div>
    </aside>
  );
}

// M05 start page (M114 when simplified); everything shown comes from /member/feed, /member/profile and /member/conversations.
function StartContent({ member }) {
  const feed = useCore("/member/feed", feedData);
  const profile = useCore("/member/profile");
  const convs = useCore("/member/conversations", (d) => d.conversations || []);
  if (feed.error) return (
    <div data-testid="start-offline">
      <V3Title title="Startseite gerade nicht verfügbar" intro="Deine aktuellen Daten konnten nicht geladen werden. Bitte versuche es erneut." testid="start-offline-title" />
      <V3Columns layout="detail" aside={<><AreaCard text="Keine aktuelle Verbindung" testid="start-offline-area" /><ResponsibleCard /></>} main={<ConnectionError onRetry={feed.reload} />} />
    </div>
  );
  if (!feed.data || (!profile.data && !profile.error)) return (
    <div data-testid="start-loading" data-m="M117" className="lg:max-w-[744px]">
      <V3Title title="Daten werden geladen" intro="Deine aktuellen Daten werden geladen." testid="start-loading-title" />
      <Loading sections={["Startseite", "Teilnahmen", "Nachrichten", "Hinweis"]} />
    </div>
  );
  if (isSimplified(profile.data)) return <SimpleStart />;
  const { announcements = [], upcoming_activities: upcoming = [], suggested_groups: groups = [] } = feed.data;
  const next = nextConfirmed(upcoming);
  const more = upcoming.filter((a) => a !== next);
  return (
    <div data-testid="member-start" data-m="M05">
      <V3Title title={greeting(member?.name, new Date().getHours())} intro={INTRO} testid="member-greeting" />
      <div className="grid grid-cols-1 items-start gap-[18px] lg:grid-cols-[minmax(0,747fr)_minmax(0,363fr)] lg:gap-x-[22px] lg:gap-y-[15px]">
        <div className="min-w-0 lg:col-start-1">{next ? <NextActivityCard a={next} /> : <NoConfirmed />}</div>
        <StartAside request={firstIncoming(convs.data)} />
        <div className="min-w-0 space-y-[22px] lg:col-start-1">
          <Announcements list={announcements} />
          <section aria-labelledby="start-upcoming" data-testid="start-upcoming">
            <div className="flex items-center justify-between gap-3"><SectionTitle id="start-upcoming">Kommende Aktivitäten</SectionTitle>
              <Link to="/aktivitaeten" data-testid="member-view-activities" className="min-h-[44px] content-center text-[14px] font-bold text-v3-magenta hover:underline">Alle ansehen</Link></div>
            {more.length ? <div className="mt-[14px] grid gap-[18px] sm:grid-cols-2">{more.map((a) => <NearbyCard key={a.id} a={a} />)}</div>
              : <p className={`${V3_CARD} mt-[14px] px-[18px] py-[16px] text-[14px] text-v3-muted`} data-testid="start-empty">Gerade gibt es keine weiteren kommenden Aktivitäten. Unter „Aktivitäten“ kannst du stöbern.</p>}
          </section>
          <SuggestedGroups groups={groups} />
        </div>
      </div>
    </div>
  );
}

// Remount all start-page reads when the member changes; old responses belong to the old view.
export default function Start() {
  const { member } = useMemberAuth();
  return <StartContent key={member?.id || "anon"} member={member} />;
}
